import type { SearchableSelectOption } from '@kss/ui';
import {
  storeService, warehouseService, companyService, productService, categoryService,
  attributeService, employeeService, userApiService, roleService,
  salesOrderService, purchaseOrderService, invoiceService, accountService, menuService,
  subscriptionPlanService, tenantService,
  customerService, supplierService, currencyService, taxService, brandService, unitService,
} from '@kss/api';

/**
 * Shared async loaders that map each entity's `.all()` response to
 * `SearchableSelectOption[]`. Use these in `CrudField.loadOptions` for
 * consistent FK-picker behaviour across every form.
 */

export const loadStoreOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await storeService.all();
  return (rows ?? []).map((s) => ({ value: s.storeId ?? '', label: s.name, description: s.code, disabled: !s.storeId }));
};

export const loadWarehouseOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await warehouseService.all();
  return (rows ?? []).map((w) => ({ value: w.warehouseId ?? '', label: w.name, description: w.code, disabled: !w.warehouseId }));
};

export const loadCompanyOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await companyService.all();
  return (rows ?? []).map((c) => ({ value: c.companyId ?? '', label: c.name, description: c.code, disabled: !c.companyId }));
};

export const loadProductOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await productService.all();
  return (rows ?? []).map((p) => ({ value: p.productId ?? '', label: p.name, description: p.sku, disabled: !p.productId }));
};

export const loadCategoryOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await categoryService.all();
  return (rows ?? []).map((c) => ({ value: c.categoryId ?? '', label: c.name, description: c.description, disabled: !c.categoryId }));
};

export const loadAttributeOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await attributeService.all();
  return (rows ?? []).map((a) => ({ value: a.attributeId ?? '', label: a.name, description: a.description, disabled: !a.attributeId }));
};

export const loadEmployeeOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await employeeService.all();
  return (rows ?? []).map((e) => {
    const full = [e.firstName, e.lastName].filter(Boolean).join(' ') || e.code || e.email || '';
    return { value: e.employeeId ?? '', label: full, description: e.designation ?? e.code, disabled: !e.employeeId };
  });
};

export const loadUserOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await userApiService.all();
  return (rows ?? []).map((u) => {
    const display = u.name ?? ([u.firstName, u.lastName].filter(Boolean).join(' ') || u.userName || u.email || u.userId || '');
    return { value: u.userId ?? '', label: display, description: u.email ?? undefined, disabled: !u.userId };
  });
};

export const loadRoleOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await roleService.all();
  return (rows ?? []).map((r) => ({ value: r.roleId ?? r.code ?? '', label: r.name, description: r.description, disabled: !r.roleId && !r.code }));
};

export const loadSalesOrderOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await salesOrderService.all();
  return (rows ?? []).map((o) => ({
    value: o.salesOrderId ?? '',
    label: o.orderNumber || o.salesOrderId || '—',
    description: o.status != null ? String(o.status) : undefined,
    disabled: !o.salesOrderId,
  }));
};

export const loadPurchaseOrderOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await purchaseOrderService.all();
  return (rows ?? []).map((p) => ({
    value: p.purchaseOrderId ?? '',
    label: p.orderNumber || p.purchaseOrderId || '—',
    description: p.status != null ? String(p.status) : undefined,
    disabled: !p.purchaseOrderId,
  }));
};

export const loadInvoiceOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await invoiceService.all();
  return (rows ?? []).map((i) => ({
    value: i.invoiceId ?? '',
    label: i.invoiceNumber || i.invoiceId || '—',
    description: i.status != null ? String(i.status) : undefined,
    disabled: !i.invoiceId,
  }));
};

export const loadAccountOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await accountService.all();
  return (rows ?? []).map((a) => ({
    value: a.accountId ?? '',
    label: a.name,
    description: a.code ?? (a.accountType != null ? String(a.accountType) : undefined),
    disabled: !a.accountId,
  }));
};

export const loadMenuOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await menuService.all();
  return (rows ?? []).map((m) => ({
    value: m.menuId ?? '',
    label: m.name,
    // Live API uses `route`; older data still has `path`.
    description: m.route ?? m.path,
    disabled: !m.menuId,
  }));
};

export const loadSubscriptionPlanOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await subscriptionPlanService.all();
  return (rows ?? []).map((p) => {
    const price =
      p.monthlyPrice != null
        ? `₹ ${p.monthlyPrice} / mo`
        : p.yearlyPrice != null
          ? `₹ ${p.yearlyPrice} / yr`
          : undefined;
    return {
      value: p.subscriptionPlanId ?? '',
      label: p.name,
      description: price,
      disabled: !p.subscriptionPlanId,
    };
  });
};

export const loadTenantOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await tenantService.all();
  return (rows ?? []).map((t) => ({
    value: t.tenantId ?? '',
    label: t.name,
    description: t.code,
    disabled: !t.tenantId,
  }));
};

export const loadCustomerOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await customerService.all();
  return (rows ?? []).map((c) => ({
    value: c.customerId ?? '',
    label: c.name,
    description: c.email ?? c.phone ?? c.code,
    disabled: !c.customerId,
  }));
};

export const loadSupplierOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await supplierService.all();
  return (rows ?? []).map((s) => ({
    value: s.supplierId ?? '',
    label: s.name,
    description: s.email ?? s.phone ?? s.code,
    disabled: !s.supplierId,
  }));
};

export const loadCurrencyOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await currencyService.all();
  return (rows ?? []).map((c) => ({
    value: c.currencyId ?? '',
    label: c.code,
    description: c.name + (c.symbol ? ` (${c.symbol})` : ''),
    disabled: !c.currencyId,
  }));
};

export const loadTaxOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await taxService.all();
  return (rows ?? []).map((t) => ({
    value: t.taxId ?? '',
    label: t.name,
    description: t.rate != null ? `${t.rate}%` : t.code,
    disabled: !t.taxId,
  }));
};

export const loadBrandOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await brandService.all();
  return (rows ?? []).map((b) => ({
    value: b.brandId ?? '',
    label: b.name,
    description: b.description,
    disabled: !b.brandId,
  }));
};

export const loadUnitOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await unitService.all();
  return (rows ?? []).map((u) => ({
    value: u.unitId ?? '',
    label: u.code,
    description: u.name,
    disabled: !u.unitId,
  }));
};
